import React from "react";
import { useParams, Link } from "@/i18n/router";
import { useQuery } from "@tanstack/react-query";
import { Calendar, User, ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { Seo } from "@/lib/seo";
import { Reveal } from "@/components/motion/Reveal";
import { PlatformCta } from "@/components/sections/PlatformCta";
import { api, SITE_URL } from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" });
}

export default function BlogPost() {
  const { slug } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["blog-post", slug],
    queryFn: async () => (await api.get(`/blog/posts/${slug}`)).data,
    retry: false,
  });

  if (isLoading) return <div className="pt-40 pb-40 text-center text-silver/50">Loading…</div>;
  if (isError || !data?.post)
    return (
      <div className="pt-40 pb-40 text-center">
        <p className="text-silver/60">Article not found.</p>
        <Link to="/blog" className="mt-4 inline-block text-astro-light">Back to blog</Link>
      </div>
    );

  const post = data.post;
  const related = data.related || [];
  const translations = data.translations || [];
  const langLabel = { en: "English", es: "Español", fr: "Français", de: "Deutsch", it: "Italiano" };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    image: post.cover_image,
    datePublished: post.published_at,
    dateModified: post.updated_at,
    author: { "@type": "Organization", name: post.author_name || "AstroLife" },
    publisher: { "@type": "Organization", name: "AstroLife" },
    mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
    articleSection: post.category,
  };

  return (
    <>
      <Seo
        title={post.seo_title || post.title}
        description={post.seo_description || post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.cover_image}
        type="article"
        locale={post.language}
        alternates={[
          { hreflang: post.language || "en", href: `${SITE_URL}/blog/${post.slug}` },
          ...translations.map((t) => ({ hreflang: t.language, href: `${SITE_URL}/blog/${t.slug}` })),
        ]}
        keywords={(post.tags || []).join(", ")}
        jsonLd={jsonLd}
      />

      <article className="bg-navy pt-[72px]">
        {/* Header */}
        <div className="relative border-b border-navy-border">
          <div className="relative h-[38vh] min-h-[260px] overflow-hidden">
            <img src={post.cover_image} alt={post.title} className="h-full w-full object-cover opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/90 to-navy/50" />
          </div>
          <div className="mx-auto -mt-32 max-w-3xl px-5 pb-4 lg:px-8 relative z-10">
            <nav aria-label="Breadcrumb" className="mb-6">
              <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-silver/60 hover:text-white transition-colors">
                <ArrowLeft className="h-4 w-4" /> Back to Journal
              </Link>
            </nav>
            <span className="text-xs font-semibold uppercase tracking-wider text-astro-light">{post.category}</span>
            <h1 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-tight text-white">
              {post.title}
            </h1>
            <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-silver/50">
              <span className="flex items-center gap-2"><User className="h-4 w-4" /> {post.author_name}</span>
              <span className="flex items-center gap-2"><Calendar className="h-4 w-4" /> {fmtDate(post.published_at)}</span>
              {post.reading_time && <span className="flex items-center gap-2"><Clock className="h-4 w-4" /> {post.reading_time} min read</span>}
            </div>
            {translations.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="post-language-switch">
                <span className="text-xs uppercase tracking-wider text-silver/40">Available in:</span>
                {translations.map((t) => (
                  <Link key={t.slug} to={`/blog/${t.slug}`} className="rounded-full border border-astro-blue/40 bg-astro-blue/10 px-3 py-1 text-xs font-semibold text-astro-light hover:bg-astro-blue hover:text-white transition-colors">
                    {langLabel[t.language] || t.language}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="mx-auto max-w-3xl px-5 py-16 lg:px-8">
          <Reveal>
            <div
              className="prose-astro max-w-none"
              data-testid="blog-post-content"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </Reveal>

          {(post.tags || []).length > 0 && (
            <div className="mt-12 flex flex-wrap gap-2 border-t border-navy-border pt-8">
              {post.tags.map((t) => (
                <span key={t} className="rounded-full border border-navy-border bg-navy-surface px-3 py-1.5 text-xs text-silver/70">#{t}</span>
              ))}
            </div>
          )}
        </div>
      </article>

      {related.length > 0 && (
        <section className="border-t border-navy-border bg-navy-surface py-20">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <h2 className="font-heading text-2xl font-bold text-white">Related articles</h2>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link key={r.id} to={`/blog/${r.slug}`} className="group block overflow-hidden rounded-2xl border border-navy-border bg-navy">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={r.cover_image} alt={r.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <span className="text-xs font-semibold uppercase tracking-wider text-astro-light">{r.category}</span>
                    <h3 className="mt-3 font-heading text-base font-semibold leading-snug text-white group-hover:text-astro-light transition-colors">{r.title}</h3>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm text-astro-light">Read <ArrowRight className="h-3.5 w-3.5" /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <PlatformCta />
    </>
  );
}
